import * as React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';

type Surface = 'blue' | 'light' | 'brand' | 'cloud';

// Element → design-system token map per surface. Keeps all CMS body copy inside
// the design system (no className tuning at call sites). Bodies are restricted
// to paragraphs, unordered lists, bold, and links — headings / ordered lists are
// out of scope and fall back to browser defaults.
const MAP: Record<Surface, { ul: string; strong: string; wrap: string }> = {
  blue: {
    ul: 't-deck-bullets t-deck-bullets--on-blue',
    strong: 'font-medium text-white',
    wrap: 'space-y-5 t-body-on-blue',
  },
  light: {
    ul: 't-deck-bullets',
    strong: 'font-medium text-primary',
    wrap: 'space-y-5 t-body',
  },
  // Brand-blue body on a white surface (t-body-blue / --primary-80). Used by the
  // About intro/connect copy, reviews intro, subscribe success, booking success,
  // and the reading deck-select prose.
  brand: {
    ul: 't-deck-bullets',
    strong: 'font-medium text-primary',
    wrap: 'space-y-5 t-body-blue',
  },
  // Lede over the 3D cloud plate (t-lede + on-cloud text-shadow), centered. Used
  // by the decks hero and bundle ledes. Children inherit the lede font/color.
  cloud: {
    ul: 't-deck-bullets',
    strong: 'font-medium text-primary',
    wrap: 'space-y-4 t-lede t-lede--on-cloud text-center',
  },
};

function buildComponents(surface: Surface): Components {
  const m = MAP[surface];
  return {
    ul: ({ children, ...props }: React.ComponentProps<'ul'>) => (
      <ul className={m.ul} {...props}>
        {children}
      </ul>
    ),
    strong: ({ children, ...props }: React.ComponentProps<'strong'>) => (
      <strong className={m.strong} {...props}>
        {children}
      </strong>
    ),
    // Link styling is intentionally NOT surface-varied: the same underline
    // treatment applies on both surfaces and the color inherits from the
    // surrounding body type.
    a: ({ children, ...props }: React.ComponentProps<'a'>) => (
      <a className="underline underline-offset-2" {...props}>
        {children}
      </a>
    ),
  };
}

// Build the react-markdown components object once per surface at module scope.
// This renderer mounts inside the scroll scene (which repaints often), so we
// must not reconstruct the components map on every render.
const COMPONENTS: Record<Surface, Components> = {
  blue: buildComponents('blue'),
  light: buildComponents('light'),
  brand: buildComponents('brand'),
  cloud: buildComponents('cloud'),
};

/** Renders a CMS Markdown body on either the on-blue services surface or the
 *  on-white surface (About section). Paragraphs and bullet lists are authored in
 *  Markdown so new list content needs no model fields. */
export function MarkdownBody({
  body,
  surface = 'blue',
}: {
  body: string;
  surface?: Surface;
}) {
  const components = COMPONENTS[surface];
  // CMS bodies arrive with CRLF; normalise so Markdown sees clean line endings.
  const src = body.replace(/\r\n/g, '\n');
  return (
    <div className={MAP[surface].wrap}>
      <ReactMarkdown components={components}>{src}</ReactMarkdown>
    </div>
  );
}
